<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <v-btn text color="primary" class="tw-mb-4" @click="$router.push({ name: 'AgentConversions' })">
          <v-icon left>mdi-arrow-left</v-icon>
          Back to Conversions
        </v-btn>

        <div v-if="loading" class="tw-w-full tw-flex tw-justify-center tw-py-20">
          <v-progress-circular :size="56" color="#00AEEF" indeterminate />
        </div>

        <template v-else-if="conversion && conversion.policy">
          <!-- Page Header -->
          <header class="page-header tw-mb-6">
            <h2 class="page-title">Conversion Details</h2>
          </header>

          <v-tabs v-model="activeTab" color="#00AEEF" class="tw-mb-6 custom-tabs">
            <v-tab font-weight="700">Overview & Risks</v-tab>
            <v-tab font-weight="700">Policy Documents</v-tab>
            <v-tab font-weight="700">Policy Transactions</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" class="tw-bg-transparent">
            <!-- TAB 1: Overview & Risks -->
            <v-tab-item>
              <!-- Conversion Info Card -->
              <div class="detail-card tw-mb-6">
                <h3 class="detail-section-title">Conversion Information</h3>
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">Client</span>
                    <span class="detail-value">{{ conversion.client || '—' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Product</span>
                    <span class="detail-value">{{ conversion.product || '—' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Policy No</span>
                    <span class="detail-value">{{ conversion.policy || '—' }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Premium</span>
                    <span class="detail-value">{{ formatCurrency(conversion.value) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Commissions</span>
                    <span class="detail-value">{{ formatAmount(conversion.commissionAmount) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Cover From</span>
                    <span class="detail-value">{{ formatDate(conversion.coverFrom) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Cover To</span>
                    <span class="detail-value">{{ formatDate(conversion.coverTo) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Renewal Date</span>
                    <span class="detail-value">{{ formatDate(conversion.renewalDate) }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Batch No</span>
                    <span class="detail-value">{{ conversion.batchNo || '—' }}</span>
                  </div>
                </div>
              </div>

              <!-- Risks Table -->
              <div class="detail-card">
                <h3 class="detail-section-title">Risks</h3>
                <div v-if="risks.length > 0" class="risks-table-wrap">
                  <table class="risks-table">
                    <thead>
                      <tr>
                        <th>Risk ID</th>
                        <th>Description</th>
                        <th>Value</th>
                        <th>Sub Class</th>
                        <th>Cover Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(r, idx) in risks" :key="idx" class="risks-row">
                        <td>{{ r.riskId || '—' }}</td>
                        <td>{{ r.riskDesc || '—' }}</td>
                        <td>{{ formatAmount(r.riskValue) }}</td>
                        <td>{{ r.subClass || '—' }}</td>
                        <td>{{ r.coverType || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="empty-risks">
                  <v-icon size="48" color="#CBD5E1">mdi-shield-outline</v-icon>
                  <p>No risks recorded for this conversion.</p>
                </div>
              </div>
            </v-tab-item>

            <!-- TAB 2: Documents -->
            <v-tab-item>
              <div class="detail-card">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-5"
                     style="padding-bottom:12px; border-bottom:2px solid #E8EDF2;">
                  <h3 class="detail-section-title tw-mb-0" style="border:none; padding:0;">
                    Policy Documents
                  </h3>
                  <span v-if="!policyDocsLoading && policyDocs.length > 0"
                        class="docs-count-badge">
                    {{ policyDocs.length }} file{{ policyDocs.length !== 1 ? 's' : '' }}
                  </span>
                </div>

                <!-- Loading -->
                <div v-if="policyDocsLoading" class="tw-flex tw-items-center tw-gap-3 tw-py-8 tw-justify-center">
                  <v-progress-circular indeterminate color="#00AEEF" size="28"></v-progress-circular>
                  <span style="color:#94A3B8; font-size:0.875rem;">Loading documents…</span>
                </div>

                <!-- Error -->
                <div v-else-if="policyDocsError" class="empty-risks">
                  <v-icon size="40" color="#FC8181">mdi-alert-circle-outline</v-icon>
                  <p>{{ policyDocsError }}</p>
                </div>

                <!-- Empty -->
                <div v-else-if="policyDocs.length === 0" class="empty-risks">
                  <v-icon size="48" color="#CBD5E1">mdi-folder-open-outline</v-icon>
                  <p>No documents found for this policy.</p>
                </div>

                <!-- Documents Grid -->
                <div v-else class="policy-docs-grid">
                  <div
                    v-for="doc in policyDocs"
                    :key="doc.id"
                    class="policy-doc-card"
                    @click="openDocument(doc)"
                  >
                    <div class="policy-doc-icon-wrap">
                      <v-icon size="28" :color="doc.mimeType === '.pdf' ? '#E53E3E' : '#718096'">
                        {{ doc.mimeType === '.pdf' ? 'mdi-file-pdf-box' : 'mdi-file-document-outline' }}
                      </v-icon>
                    </div>
                    <div class="policy-doc-body">
                      <p class="policy-doc-name">{{ doc.actualName }}</p>
                      <span class="policy-doc-type">{{ doc.docType }}</span>
                    </div>
                    <div class="policy-doc-footer">
                      <span class="policy-doc-date">{{ doc.dateCreated }}</span>
                      <v-btn x-small depressed color="primary" class="policy-doc-btn">
                        <v-icon x-small left>mdi-eye-outline</v-icon> View
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- TAB 3: Policy Transactions -->
            <v-tab-item>
              <div class="detail-card">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-5"
                     style="padding-bottom:12px; border-bottom:2px solid #E8EDF2;">
                  <h3 class="detail-section-title tw-mb-0" style="border:none; padding:0;">
                    Policy Transactions
                  </h3>
                  <v-btn
                    v-if="transactions.length > 0"
                    small
                    color="primary"
                    depressed
                    @click="exportTransactionsToPdf"
                    :loading="generatingPdf"
                  >
                    <v-icon left size="16">mdi-file-pdf-box</v-icon>
                    Export to PDF
                  </v-btn>
                </div>

                <!-- Loading -->
                <div v-if="transactionsLoading" class="tw-flex tw-items-center tw-gap-3 tw-py-8 tw-justify-center">
                  <v-progress-circular indeterminate color="#00AEEF" size="28"></v-progress-circular>
                  <span style="color:#94A3B8; font-size:0.875rem;">Loading transactions…</span>
                </div>

                <!-- Error -->
                <div v-else-if="transactionsError" class="empty-risks">
                  <v-icon size="40" color="#FC8181">mdi-alert-circle-outline</v-icon>
                  <p>{{ transactionsError }}</p>
                </div>

                <!-- Empty -->
                <div v-else-if="transactions.length === 0" class="empty-risks">
                  <v-icon size="48" color="#CBD5E1">mdi-currency-usd-off</v-icon>
                  <p>No transactions found for this policy.</p>
                </div>

                <!-- Transactions Table -->
                <div v-else class="risks-table-wrap">
                  <table class="risks-table transaction-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Our Ref</th>
                        <th>Trans Amt</th>
                        <th>S.Duty</th>
                        <th>T.Levy</th>
                        <th>PHCF</th>
                        <th>Gross</th>
                        <th>Allocated</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(t, idx) in transactions" :key="idx" class="risks-row">
                        <td class="tw-whitespace-nowrap">{{ t.transDate }}</td>
                        <td>
                          <span :class="['type-badge', t.type === 'Receipts' ? 'receipt' : 'debit']">
                            {{ t.type }}
                          </span>
                        </td>
                        <td class="tw-font-mono tw-text-[11px]">{{ t.ourRef }}</td>
                        <td class="tw-font-bold">{{ formatAmount(t.transactionAmt) }}</td>
                        <td>{{ formatAmount(t.stampDuty) }}</td>
                        <td>{{ formatAmount(t.trainingLevy) }}</td>
                        <td>{{ formatAmount(t.phcf) }}</td>
                        <td class="tw-font-bold">{{ formatAmount(t.grossAmount) }}</td>
                        <td class="tw-text-green-600">{{ formatAmount(t.allocatedAmount) }}</td>
                        <td class="tw-font-bold tw-text-[#1E4276]">{{ formatAmount(t.balance) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </template>

        <!-- Not Found -->
        <div v-else class="empty-state tw-py-16">
          <v-icon size="60" color="#CBD5E1">mdi-file-document-outline</v-icon>
          <p class="empty-title">Conversion not found</p>
          <p class="empty-subtitle">Please go back and select a conversion from the list.</p>
          <v-btn color="primary" outlined class="tw-mt-4" @click="$router.push({ name: 'AgentConversions' })">
            Back to Conversions
          </v-btn>
        </div>

      </div>
    </div>

    <!-- Document Viewer Modal -->
    <v-dialog
      v-model="docViewerVisible"
      max-width="1100"
      max-height="90vh"
      content-class="doc-viewer-dialog"
      @click:outside="closeDocViewer"
    >
      <v-card class="doc-viewer-card">
        <!-- Header -->
        <div class="doc-viewer-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <v-icon color="#E53E3E" size="22">mdi-file-pdf-box</v-icon>
            <div>
              <h3 class="doc-viewer-title">{{ docViewerTitle }}</h3>
              <p class="doc-viewer-subtitle">Document Preview</p>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <v-btn
              v-if="docViewerUrl"
              small outlined color="primary"
              @click="openDocFull"
            >
              <v-icon x-small left>mdi-open-in-new</v-icon>
              Open Full
            </v-btn>
            <v-btn icon small @click="closeDocViewer">
              <v-icon size="20" color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>

        <!-- Loading state -->
        <div v-if="docViewerLoading" class="doc-viewer-loading">
          <v-progress-circular indeterminate color="#00AEEF" size="48"></v-progress-circular>
          <p class="tw-mt-4" style="color:#94A3B8; font-size:0.9rem;">Loading document…</p>
        </div>

        <!-- iframe -->
        <div v-else-if="docViewerUrl" class="doc-viewer-frame-wrap">
          <iframe
            :src="docViewerUrl"
            class="doc-viewer-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </v-card>
    </v-dialog>
  </agent-default>
</template>

<script>
import axios from 'axios';
import AgentDefault from '@/components/layout/AgentDefault';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentConversionDetails',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      conversion: null,
      policyDocs: [],
      policyDocsLoading: false,
      policyDocsError: null,
      docViewerVisible: false,
      docViewerUrl: '',
      docViewerTitle: '',
      docViewerLoading: false,
      activeTab: 0,
      transactions: [],
      transactionsLoading: false,
      transactionsError: null,
      generatingPdf: false,
      agentCode: null,
      agentSummaryLoading: false,
    };
  },
  computed: {
    policySlug() {
      return this.$route.params.policyNo || '';
    },
    risks() {
      const list = this.conversion?.risks;
      return Array.isArray(list) ? list : [];
    },
  },
  watch: {
    policySlug: {
      immediate: true,
      handler() {
        this.loadConversion();
      },
    },
  },
  mounted() {
    this.loadConversion();
  },
  methods: {
    loadConversion() {
      this.loading = true;
      try {
        const key = this.policySlug ? `agentConversion_${this.policySlug}` : null;
        const raw = key ? sessionStorage.getItem(key) : null;
        const parsed = raw ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : null;
        this.conversion = parsed && parsed.policy ? parsed : null;
        if (this.conversion?.policy) {
          this.fetchPolicyDocuments(this.conversion.policy);

          // Try to get agnCode: priority given to conversion object, then summary API
          if (this.conversion.agentCode) {
            this.agentCode = this.conversion.agentCode;
            this.fetchTransactions();
          } else {
            this.fetchAgentSummary();
          }
        }
      } finally {
        this.loading = false;
      }
    },

    async fetchTransactions() {
      const agnCode = this.agentCode || this.conversion?.agentCode || '';
      if (!agnCode || !this.conversion?.batchNo) return;
      this.transactionsLoading = true;
      this.transactionsError = null;
      try {
        const res = await axios.get(
          `/customer/gis/agent/policyTransactions?agnCode=${agnCode}&batchNo=${this.conversion.batchNo}`,
          { validateStatus: () => true },
        );
        if (res.status === 200 && res.data && Array.isArray(res.data.object)) {
          this.transactions = res.data.object;
        } else {
          this.transactionsError = 'Unable to load policy transactions.';
        }
      } catch (err) {
        this.transactionsError = 'An error occurred fetching transactions.';
      } finally {
        this.transactionsLoading = false;
      }
    },

    async fetchAgentSummary() {
      const user = this.$store?.getters?.['auth/authenticatedUser'];
      const email = user?.email || '';
      if (!email) return;

      this.agentSummaryLoading = true;
      try {
        const res = await axios.get(`/customer/gis/agent/dashboard/summary?email=${email}`);
        if (res.status === 200 && res.data?.agent?.code) {
          this.agentCode = res.data.agent.code;
          this.fetchTransactions();
        } else {
          this.transactionsError = 'Could not identify agent code.';
        }
      } catch (err) {
        console.error('Fetch Agent Summary Error:', err);
      } finally {
        this.agentSummaryLoading = false;
      }
    },

    async fetchPolicyDocuments(policyNo) {
      if (!policyNo) return;
      this.policyDocsLoading = true;
      this.policyDocsError = null;
      try {
        const res = await axios.get(
          `/customer/document-api/v1/getNBDocByPolicyNo?policyNo=${policyNo}`,
          { validateStatus: () => true },
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          const ALLOWED = ['.pdf', '.doc', '.docx'];
          this.policyDocs = res.data.filter(
            (doc) => ALLOWED.includes((doc.mimeType || '').toLowerCase()),
          );
        } else {
          this.policyDocsError = 'Unable to load documents.';
        }
      } catch {
        this.policyDocsError = 'An error occurred fetching documents.';
      } finally {
        this.policyDocsLoading = false;
      }
    },

    openDocument(doc) {
      const docId = typeof doc === 'object' ? doc.id : doc;
      const docName = typeof doc === 'object' ? (doc.actualName || `Document #${docId}`) : `Document #${docId}`;
      trackAgentInteraction(this, 'conversion_details_open_document', {
        document_id: docId,
        document_name: docName,
      });

      this.docViewerTitle = docName;
      this.docViewerLoading = false;
      const basePath = process.env.NODE_ENV === 'production' ? '/api' : '';
      this.docViewerUrl = `${basePath}/customer/document-api/v1/viewUWDocs/${docId}`;
      this.docViewerVisible = true;
    },

    closeDocViewer() {
      this.docViewerVisible = false;
      this.docViewerUrl = '';
    },
    openDocFull() {
      if (this.docViewerUrl) {
        trackAgentInteraction(this, 'conversion_details_open_document_full', {
          document_name: this.docViewerTitle || 'unknown',
        });
        window.open(this.docViewerUrl, '_blank', 'noopener,noreferrer');
      }
    },
    formatDate(val) {
      if (!val) return '—';
      try {
        return new Date(val).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return val;
      }
    },
    formatCurrency(value) {
      if (value == null) return '';
      return `Ksh ${Number(value).toLocaleString()}`;
    },
    formatAmount(val) {
      if (val == null || val === undefined || val === '') return '—';
      return Number(val).toLocaleString();
    },

    async exportTransactionsToPdf() {
      if (this.transactions.length === 0) return;
      trackAgentInteraction(this, 'conversion_details_export_transactions_pdf', {
        rows: this.transactions.length,
      });
      this.generatingPdf = true;
      try {
        const { jsPDF: JsPDF } = window.jspdf;
        const doc = new JsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });

        // Header
        doc.setFontSize(16);
        doc.setTextColor(30, 66, 118); // #1E4276
        doc.text('Policy Transactions Report', 14, 20);

        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139); // #64748b
        doc.text(`Policy No: ${this.conversion.policy}`, 14, 28);
        doc.text(`Client: ${this.conversion.client}`, 14, 33);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 38);

        // Simple table implementation using autoTable if available, but usually we build manual rows or expect autoTable
        // Assuming autoTable is available as it's common with jsPDF in this context
        const head = [['Date', 'Type', 'Our Ref', 'Amt', 'S.Duty', 'T.Levy', 'PHCF', 'Comm', 'Gross', 'Allocated', 'Balance']];
        const body = this.transactions.map((t) => [
          t.transDate,
          t.type,
          t.ourRef,
          this.formatAmount(t.transactionAmt),
          this.formatAmount(t.stampDuty),
          this.formatAmount(t.trainingLevy),
          this.formatAmount(t.phcf),
          this.formatAmount(t.commission),
          this.formatAmount(t.grossAmount),
          this.formatAmount(t.allocatedAmount),
          this.formatAmount(t.balance),
        ]);

        if (typeof doc.autoTable === 'function') {
          doc.autoTable({
            startY: 45,
            head,
            body,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [30, 66, 118] },
          });
        } else if (window.jspdf && typeof window.jspdf.autoTable === 'function') {
          window.jspdf.autoTable(doc, {
            startY: 45,
            head,
            body,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [30, 66, 118] },
          });
        } else {
          doc.text('Table plugin could not be initialized.', 14, 50);
        }

        doc.save(`Transactions_${this.conversion.policy}.pdf`);
      } catch (err) {
        console.error('PDF Export Error:', err);
      } finally {
        this.generatingPdf = false;
      }
    },
  },
  metaInfo: {
    title: 'Conversion Details',
  },
};
</script>

<style scoped>
.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0;
}

.detail-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  padding: 24px;
}

.detail-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #E8EDF2;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 20px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1E4276;
}

.risks-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.risks-table {
  width: 100%;
  min-width: 600px;
  border-collapse: collapse;
}

.risks-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.risks-table th {
  text-align: left;
  padding: 12px 14px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.risks-row {
  border-bottom: 1px solid #F1F5F9;
}

.risks-row:hover {
  background: #F8FAFC;
}

.risks-table td {
  padding: 14px;
  font-size: 0.875rem;
  color: #334155;
}

.empty-risks {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #94A3B8;
  font-size: 0.95rem;
}

.empty-risks p {
  margin: 12px 0 0 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

.docs-count-badge {
  background: #EBF8FF; color: #2B6CB0;
  font-size: 0.75rem; font-weight: 700;
  padding: 3px 10px; border-radius: 20px;
}
.policy-docs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}
.policy-doc-card {
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex; flex-direction: column; gap: 8px;
}
.policy-doc-card:hover {
  border-color: #00AEEF;
  box-shadow: 0 4px 12px rgba(0,174,239,0.12);
  transform: translateY(-2px);
}
.policy-doc-icon-wrap { display: flex; align-items: center; }
.policy-doc-body { flex: 1; }
.policy-doc-name {
  font-size: 0.825rem; font-weight: 700;
  color: #1E4276; margin: 0 0 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.policy-doc-type {
  font-size: 0.72rem; font-weight: 600;
  background: #EBF8FF; color: #2B6CB0;
  padding: 2px 8px; border-radius: 10px;
}
.policy-doc-footer {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 4px;
}
.policy-doc-date { font-size: 0.72rem; color: #94A3B8; }

.doc-viewer-card {
  border-radius: 14px !important;
  display: flex;
  flex-direction: column;
  /* Use viewport height so it's readable */
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
}
.doc-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #FAFBFC;
  flex-shrink: 0;
}
.doc-viewer-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0;
}
.doc-viewer-subtitle {
  font-size: 0.72rem;
  color: #94A3B8;
  margin: 0;
}
.doc-viewer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
}
.doc-viewer-frame-wrap {
  flex: 1;
  overflow: hidden;
  display: flex;
}
.doc-viewer-iframe {
  width: 100%;
  /* Fill the full card height */
  height: 100%;
  min-height: 600px;
  border: none;
  display: block;
  flex: 1;
}
.doc-viewer-dialog {
  border-radius: 14px !important;
  overflow: hidden;
  /* Make it tall enough to read without opening full */
  margin: 24px auto !important;
}

.custom-tabs {
  border-bottom: 1px solid #E8EDF2;
}

::v-deep .custom-tabs .v-tab {
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
  font-size: 0.9rem !important;
}

.type-badge {
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.type-badge.receipt {
  background: #DCFCE7;
  color: #166534;
}

.type-badge.debit {
  background: #FEE2E2;
  color: #991B1B;
}

.transaction-table th {
  background: #F1F5F9 !important;
}
</style>
